import { useRef, useState } from 'react';
import {
  Stack,
  Text,
  Flex,
  Input,
  Button,
  Icon,
  useToast,
} from '@pairy/pds-react';
import { useSetAtom } from 'jotai';

import usePayCouponValidate from '@/hooks/queries/pay/usePayCouponValidate';
import { PlanItemType } from '@/hooks/queries/pay/usePayPlan';

import {
  CouponAppliedAtom,
  PricePlanInfoAtom,
  SelectedPlanAtom,
} from '../index.atom';
import { COUPON_LENGTH_LIMIT } from '../constant';

import * as Styled from './index.styles';

const CouponInput = () => {
  const toast = useToast();

  const couponInputRef = useRef<HTMLDivElement | null>(null);

  const setIsCouponApplied = useSetAtom(CouponAppliedAtom);
  const setPricePlanInfo = useSetAtom(PricePlanInfoAtom);
  const setSelectedPlan = useSetAtom(SelectedPlanAtom);

  const [isOpen, setIsOpen] = useState(false);
  const [coupon, setCoupon] = useState('');

  const [errorMessage, setErrorMessage] = useState<null | string>(null);

  const handlePayPlanStatus = (
    plan: Omit<PlanItemType, 'status'>[],
  ): PlanItemType[] => {
    return plan.map((item) => {
      return {
        ...item,
        status: 'new',
      };
    });
  };

  const { mutate } = usePayCouponValidate({
    onSuccess: (data) => {
      setPricePlanInfo(handlePayPlanStatus(data.data.data.plan));
      setIsCouponApplied(true);
      setSelectedPlan(null);
      toast('쿠폰이 성공적으로 적용되었습니다');
    },
    onError: (error) => {
      setIsCouponApplied(false);
      setErrorMessage('error');

      if (error.response?.status === 404) {
        setErrorMessage('유효하지 않은 쿠폰입니다');
        toast.error('유효하지 않은 쿠폰입니다');
      } else if (error.response?.status === 400) {
        setErrorMessage('이미 사용한 쿠폰입니다');
        toast.error('이미 사용한 쿠폰입니다');
      } else if (error.response?.status === 409) {
        setErrorMessage('이미 플랜을 구독중 입니다');
        toast.error('이미 플랜을 구독중 입니다');
      } else {
        setErrorMessage('알 수 없는 에러입니다 세일즈클루팀에 문의해주세요');
      }
    },
    retry: false,
  });

  const handleConfirmButtonClick = () => {
    mutate({ code: coupon.trim() });
  };

  return (
    <Stack gap={10} direction="column" padding={{ x: 16, y: 12 }}>
      <Flex justifyContent="center">
        <Styled.CouponTriggerWrapper
          gap={8}
          width="fit-content"
          justifyContent="center"
          alignItems="center"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
            if (couponInputRef.current) {
              couponInputRef.current.scrollIntoView();
            }
          }}
          padding={{ x: 12, y: 8 }}
        >
          <Text variant="md-medium" color="skyblue_400">
            쿠폰 입력하기
          </Text>
          <Styled.RotateArrowWrapper className={isOpen ? 'is-top' : 'is-down'}>
            <Icon icon="ph:caret-down" size={17} color="skyblue_400" />
          </Styled.RotateArrowWrapper>
        </Styled.CouponTriggerWrapper>
      </Flex>

      <Styled.CouponContainer
        gap={8}
        direction="column"
        width="100%"
        className={isOpen ? 'is-visible' : ''}
      >
        <Text variant="sm-medium" color="zinc_400">
          쿠폰번호
        </Text>
        <div ref={couponInputRef}>
          <Flex justifyContent="space-between">
            <Input
              width="80%"
              value={coupon}
              isError={errorMessage !== null}
              onFocus={() => {
                setErrorMessage(null);
              }}
              onChange={(e) => {
                setCoupon(e.target.value);
              }}
            />

            <Button
              variant="skyblue_400_fill"
              disabled={
                coupon.length < COUPON_LENGTH_LIMIT || errorMessage !== null
              }
              onClick={handleConfirmButtonClick}
            >
              확인
            </Button>
          </Flex>
        </div>
        {errorMessage !== null && (
          <Text
            as="p"
            color="cerise_300"
            variant="sm-regular"
            marginTop={8}
            role="alert"
          >
            {errorMessage}
          </Text>
        )}
      </Styled.CouponContainer>
    </Stack>
  );
};

export default CouponInput;
